// src/redux/utils/riskFormUtils.js

import _ from 'lodash';

import { useSelector } from 'react-redux';
import { useMemo } from 'react';

/**
 * Merges updated riskSources data into existing riskFormData.
 * Allows creation, updating, and deletion of categories and risk sources within them.
 * @param {Object} existingRiskFormData - The current riskFormData.
 * @param {Object} updatedRiskSources - The updated riskSources configuration.
 * @returns {Object} - The merged riskFormData with updated riskSources.
 */
export const mergeRiskSources = (existingRiskFormData, updatedRiskSources) => {
  // Clone existing riskFormData to avoid mutations
  const mergedRiskFormData = _.cloneDeep(existingRiskFormData);

  // Iterate over each category in the updatedRiskSources
  Object.keys(updatedRiskSources).forEach((category) => {
    const updatedCategory = updatedRiskSources[category];
    const existingCategory = mergedRiskFormData[category];

    if (!existingCategory) {
      // **Addition**: Add the entire new category
      mergedRiskFormData[category] = _.cloneDeep(updatedCategory);
    } else {
      // **Update**: Merge risk sources within the existing category
      const existingRiskSources = existingCategory.riskSources || [];
      const updatedRiskSourcesArray = updatedCategory.riskSources || [];

      // Create a map for existing and updated risk sources by name
      const existingRiskSourcesMap = _.keyBy(existingRiskSources, 'name');
      const updatedRiskSourcesMap = _.keyBy(updatedRiskSourcesArray, 'name');

      const mergedRiskSources = [];

      // Iterate over existing risk sources to handle updates and deletions
      Object.keys(existingRiskSourcesMap).forEach((riskSourceName) => {
        if (updatedRiskSourcesMap[riskSourceName]) {
          // **Update**: Merge existing risk source with updated risk source
          const updatedRiskSource = updatedRiskSourcesMap[riskSourceName];
          const existingRiskSource = existingRiskSourcesMap[riskSourceName];

          const mergedRiskSource = _.mergeWith(
            {},
            existingRiskSource, // Start with existing data
            updatedRiskSource,  // Apply updates
            (objValue, srcValue, key) => {
              if (key === 'value') {
                return objValue; // Preserve existing 'value'
              }
              return undefined; // Use default merging for other keys
            }
          );

          mergedRiskSources.push(mergedRiskSource);

          // Remove from updated map to identify new additions
          delete updatedRiskSourcesMap[riskSourceName];
        }
        // **Deletion**: If risk source not in updated map, omit it (i.e., delete)
      });

      // **Addition**: Add any new risk sources that were not in existing ones
      const newRiskSources = _.values(updatedRiskSourcesMap);
      if (newRiskSources.length > 0) {
        mergedRiskSources.push(...newRiskSources);
      }

      // Assign the merged risk sources back to the category
      mergedRiskFormData[category].riskSources = mergedRiskSources;
    }
  });

  // **Deletion**: Remove entire categories that no longer exist in updatedRiskSources
  Object.keys(mergedRiskFormData).forEach((category) => {
    // Skip if the key is not a category (e.g., 'riskFormData' itself)
    if (category === 'riskFormData') return;

    if (!updatedRiskSources.hasOwnProperty(category)) {
      delete mergedRiskFormData[category];
    }
  });

  return mergedRiskFormData;
};

// src/redux/utils/riskFormUtils.js

/**
 * Merges updated riskRootCauses data into existing riskFormData.
 * Updates the 'causes' field of each riskSource within each category.
 * @param {Object} existingRiskFormData - The current riskFormData.
 * @param {Object} updatedRiskRootCauses - The updated riskRootCauses configuration.
 * @returns {Object} - The merged riskFormData with updated causes in each riskSource.
 */
export const mergeRiskRootCauses = (existingRiskFormData, updatedRiskRootCauses) => {
  // Clone existing riskFormData to avoid mutations
  const mergedRiskFormData = _.cloneDeep(existingRiskFormData);

  // Iterate over each category in riskFormData
  Object.keys(mergedRiskFormData).forEach((category) => {
    const categoryData = mergedRiskFormData[category];
    const riskSourcesArray = categoryData.riskSources;

    if (!Array.isArray(riskSourcesArray)) {
      console.warn(`riskSources for category "${category}" is not an array.`);
      return;
    }

    // Iterate over each riskSource in the category
    riskSourcesArray.forEach((riskSource) => {
      if (!riskSource.causes) {
        // Initialize causes if not present
        riskSource.causes = {};
      }

      const existingCauses = riskSource.causes;
      const updatedCauses = updatedRiskRootCauses;

      // Merge the updatedRiskRootCauses into existingCauses
      Object.keys(updatedCauses).forEach((causeKey) => {
        const updatedCause = updatedCauses[causeKey];
        const existingCause = existingCauses[causeKey];

        if (!existingCause) {
          // **Addition**: Add the entire cause if it doesn't exist
          existingCauses[causeKey] = _.cloneDeep(updatedCause);
        } else {
          // **Update**: Merge existing properties within the cause
          existingCauses[causeKey] = _.mergeWith(
            {},
            existingCause,      // Start with existing cause data
            updatedCause,       // Apply updates
            (objValue, srcValue, key) => {
              if (key === 'value') {
                return objValue; // Preserve existing 'value'
              }
              return undefined; // Use default merging for other keys
            }
          );
        }
      });

      // **Removal**: Remove causes that no longer exist in updatedRiskRootCauses
      Object.keys(existingCauses).forEach((causeKey) => {
        if (!updatedRiskRootCauses.hasOwnProperty(causeKey)) {
          delete existingCauses[causeKey];
        }
      });
    });
  });

  return mergedRiskFormData;
};

export const mergeRiskCauseEvaluation = (rfData, evalCfg = {}) => {
  // --- DEBUG LOGGING ---
  try {
    console.log('[mergeRiskCauseEvaluation] START');
    console.log('  » rfData keys:', rfData ? Object.keys(rfData) : 'rfData is', rfData);
    console.log('  » evalCfg received:', JSON.stringify(evalCfg, null, 2));
  } catch (e) {
    /* eslint-disable no-console */
    console.warn('[mergeRiskCauseEvaluation] logging failed:', e);
  }
  // Accept configuration provided either as
  // { defaultCriteria: { ... } }  **or** the criteria object directly.
  const template = evalCfg && evalCfg.defaultCriteria
    ? evalCfg.defaultCriteria           // wrapped form
    : evalCfg;                          // raw criteria object

  if (!template || !Object.keys(template).length) return rfData;

  const cloned = _.cloneDeep(rfData);

  Object.values(cloned).forEach(area => {
    (area.riskSources || []).forEach(src => {
      Object.entries(src.causes || {}).forEach(([cKey, cObj]) => {
        /* ---------- fast-track ---------- */
        if (!cObj.evaluation)
          cObj.evaluation = _.cloneDeep(template);

        /* ---------- deep-dive ---------- */
        if (Array.isArray(cObj.options)) {
          cObj.deepDive = cObj.deepDive || {};
          cObj.options.forEach(opt => {
            if (!cObj.deepDive[opt]) {
              cObj.deepDive[opt] = {
                value: false,                // or [] if multiselect inside
                evaluation: _.cloneDeep(template)
              };
            }
          });
        }
      });
    });
  });

  // --- DEBUG LOGGING END ---
  try {
    console.log('[mergeRiskCauseEvaluation] merged data sample:', 
      Object.entries(cloned).slice(0, 1)); // log only first area to avoid noise
    console.log('[mergeRiskCauseEvaluation] END');
  } catch (e) {
    console.warn('[mergeRiskCauseEvaluation] end‑logging failed:', e);
  }
  return cloned;
};

/**
 * React hook – returns riskFormData with evaluation criteria injected.
 * It mirrors `useMergedSecurityForm` used by SecurityRiskAssessment.
 *
 * @param {string} modelId – current model id
 */
export const useMergedRiskFormData = (modelId) => {
  const rfData   = useSelector(
    s => s.risks.models[modelId]?.riskFormData || {}
  );
  const evalCfg  = useSelector(
    s => s.config.configs['riskCauseEvaluation'] || {}
  );

  // memoise to avoid unnecessary re-renders
  return useMemo(
    () => mergeRiskCauseEvaluation(rfData, evalCfg),
    [rfData, evalCfg]
  );
};